<template>
    <v-form @submit.prevent="printReport" ref="form">
        <v-card class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">Timesheet Reports</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text>
                <v-layout row wrap> 
                    <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                        Error encountered. Please contact support.
                    </v-alert> 
                    <v-flex xs12 sm12 class="form-content">
                        <v-select
                            label="Select report"
                            :items="report_types_filtered"
                            :rules="[rules.required]" 
                            item-text="name"
                            item-value="code"
                            v-model="reportCode"           
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm12 class="form-content">
                        <v-alert v-if="selected_report" :value="true" color="info" icon="info" outline>
                            {{selected_report.description}}
                        </v-alert>  
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions height="45px">
                <v-spacer></v-spacer>
                <v-btn v-if="selected_report" color="primary" type="submit" :disabled="disabled||!reportCode" :loading="loading" class="mr-20">
                    <v-icon>{{selected_report.icon}}</v-icon>&nbsp;&nbsp;&nbsp;{{selected_report.action}}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import pageKeys from '@/common/pagekeys'
import timesheetApi from '@/api/timesheet'
import AppPageID from '@/constants/app_page_ids'
import { hasTeamExportRights } from '@/helpers/myteams-permissions'

export default {
    data () {
        return {
            error: false,
            errorMessage: null,
            reportCode: null,
            loading: false,
            disabled: false,

            //future updates - these reports will be managed in Reporting & Analytics page
            //enforceExportPermission is temporary only
            //allowedPermissions will be used in the future
            report_types: [
                {
                    code: 'SUMMARY',
                    file_type: 'XLSX',
                    action: 'Export to XLSX',
                    name: 'Timesheet Summary Report',
                    icon: 'fas fa-file-excel',
                    description: `A consolidated overview of timesheet data for all employees.`,
                    enforceExportPermission: false
                },
                {
                    code: 'DETAILED',
                    file_type: 'XLSX',
                    action: 'Export to XLSX',
                    name: 'Timesheet Detailed Report',
                    icon: 'fas fa-file-excel',
                    description: 'Detailed per-employee and per-workshift view of attendance, chargeable hours, night diff, overtime and payroll details (if available).',
                    enforceExportPermission: false
                },
                {
                    code: 'CONTROL1', 
                    file_type: 'XLSX', 
                    action: 'Export as XLSX',
                    name: 'Payroll Control Report', 
                    icon: 'fas fa-file-excel', 
                    description: 'Summary of payroll per employee in excel format.',
                    enforceExportPermission: true //only true will be restricted by hasExportRights
                },
            ],
            rules: {
                required: value => !!value || 'Required field'
            },
            selectedCutOffId: null,
            selectedTeamId: null,
            selectedPeriod: null
        }   
    },
    methods: {
        setParam(data) {
            this.$refs.form.reset()
            this.selectedCutOffId = data.selectedCutOffId
            this.selectedTeamId = data.selectedTeamId
            this.selectedPeriod = data.selectedPeriod
        },
        printReport() {
            try {
                if (this.$refs.form.validate()) {
                    if (this.reportCode=='SUMMARY') this.generateSummary()
                    if (this.reportCode=='DETAILED') this.generateDetailed()
                    if (this.reportCode=='CONTROL1') this.getControlReport()
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async generateSummary() {
            try {
                this.loading = true
                const response = await timesheetApi.getExcelTeamReport(this.selectedCutOffId, this.selectedTeamId, pageKeys.timesheet_team_approver)
                var blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
                var link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download =  this.reportFileName
                link.click()
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async generateDetailed() {
            try {
                this.loading = true
                const response = await timesheetApi.getExcelDetailedReport(this.selectedCutOffId, this.selectedTeamId, pageKeys.timesheet_team_approver)
                var blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
                var link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download = this.reportFileName
                link.click()
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getControlReport() {
            try {
                this.loading = true
                const response = await timesheetApi.getControlReportByTeam(this.selectedCutOffId, this.selectedTeamId)
                var blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
                var link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download = `Control-Report-${this.teamName}-${this.selectedPeriod}.xlsx`
                link.click()
                this.loading = false
            }catch(error){
                this.errorMessage = error
                this.error = true
            }
        },
        closeDialog() {
            this.$emit('update:dialogReports', false)
        },
    },
    computed: {
        hasTeamExportRights() {
            return hasTeamExportRights(AppPageID.TeamTimesheet)            
        },
        selected_report() {
            if (this.reportCode){
                return this.report_types.find(x => x.code == this.reportCode)
            }
        },
        teamName() {
            return this.$store.state.selectedTeamName
        },
        reportFileName() {
            if (this.selectedTeamId && this.selectedTeamId != "0") {
                return `TIMESHEET-${this.selected_report.code}-${this.teamName}-${this.selectedPeriod}.xlsx`
            }else{
                return `TIMESHEET-${this.selected_report.code}-${this.selectedPeriod}.xlsx`
            }
        },
        report_types_filtered() {
            let reports = []
            this.report_types.forEach(item => {
                if (!item.enforceExportPermission || (item.enforceExportPermission && this.hasTeamExportRights)) {
                    reports.push(item)
                } 
            })
            return reports
        }
    },
    created() {}
}
</script>
<style scoped>
.form-content {
    padding: 0px 15px 0px 15px; 
}
</style>